import {
  Avatar,
  Block,
  Box,
  Button,
  Card,
  Heading,
  Icon,
  NavList,
} from '@deckee/deck-hand';
import React, { FC, MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { captureEvent } from '../../../services/event-capture';
import { getStoredImage, getWatercraftImage } from '../../../services/image';
import {
  getWatercraftString,
  isVesselFloatPlanReady,
} from '../../../services/log-trips/float-plan';
import {
  ROUTE_BOAT_DETAILS_CREATE,
  ROUTE_BOAT_DETAILS_EDIT,
} from '../../../services/route/route-names';
import { transitionToNextRoute } from '../../../services/route/route-navigation';
import { setVessel } from '../../../services/store/modules/float-plan';
import { selectPlanIsLoggingOn } from '../../../services/store/modules/plan';
import { Vessel } from '../../../services/store/modules/vessels/models';
import BasicDrawerLayout from '../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../layouts/components/LayoutBody/LayoutBody';
import VesselListItem from './components/VesselListItem';
import useVessels from './hooks/use-vessels';

interface SelectVesselPageProps {
  onSelectVessel: (vessel: Vessel | undefined) => void;
  onBack: () => void;
}

const SelectVesselPage: FC<SelectVesselPageProps> = ({
  onSelectVessel,
  onBack,
}) => {
  const { vessels, selectVessel: onVesselSelect } = useVessels();
  const history = useHistory();
  const isLoggingOn = useSelector(selectPlanIsLoggingOn);
  const theme = useTheme();

  const handleAddBoatOnClick = async () => {
    captureEvent({
      type: 'float: vessel',
      payload: {
        selection: 'added new',
      },
    });

    transitionToNextRoute(ROUTE_BOAT_DETAILS_CREATE);
  };

  const handleAddVessel = () => {
    captureEvent({
      type: 'select vessel feature: click add vessel',
      payload: {},
    });
    history.push('/maps/settings/account/boats/new');
  };

  const handleOnSelect = (vessel: Vessel | undefined) => {
    if (vessel) {
      // set selected vessel in store
      onVesselSelect(vessel);
      // set selected vessel in float plan in store
      setVessel(vessel);

      if (
        vessel?.isInvalidLoggedOnVessel ||
        !isVesselFloatPlanReady(vessel, isLoggingOn)
      ) {
        transitionToNextRoute(ROUTE_BOAT_DETAILS_EDIT, {
          id: vessel.id,
        });
      } else {
        captureEvent({
          type: 'select vessel feature: continue',
          payload: {},
          sendToAmplitude: true,
        });
        onSelectVessel(vessel);
      }
    }
  };

  const handleEditVessel = (
    event: MouseEvent<HTMLElement>,
    vessel: Vessel | undefined,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (vessel) {
      captureEvent({
        type: 'select vessel feature: click edit vessel',
        payload: {
          vessel,
        },
      });
      transitionToNextRoute(ROUTE_BOAT_DETAILS_EDIT, {
        id: vessel.id,
      });
    }
  };

  const getAvatarImage = (vessel: Vessel) => (
    <Box
      onClick={(event: MouseEvent<HTMLElement>) =>
        handleEditVessel(event, vessel)
      }
    >
      {vessel?.image !== null ? (
        <Avatar src={getStoredImage(vessel?.image, 40)}>
          <Avatar.Indicator
            bg="white"
            border="1px solid"
            borderColor={theme.colors.success}
            onClick={(event: MouseEvent<HTMLElement>) =>
              handleEditVessel(event, vessel)
            }
          >
            <Icon
              name="pencil"
              fill={theme.colors.success}
              height={12}
              width={16}
            />
          </Avatar.Indicator>
        </Avatar>
      ) : (
        <Block position="relative">
          {getWatercraftImage(vessel.watercraft_type, 40)}

          <Avatar.Indicator
            bg="white"
            border="1px solid"
            borderColor={theme.colors.success}
            onClick={(event: MouseEvent<HTMLElement>) =>
              handleEditVessel(event, vessel)
            }
          >
            <Icon
              name="pencil"
              fill={theme.colors.success}
              height={12}
              width={16}
            />
          </Avatar.Indicator>
        </Block>
      )}
    </Box>
  );

  const showDescription = (vessel: Vessel): React.ReactNode => {
    if (
      (isLoggingOn && vessel?.isInvalidLoggedOnVessel) ||
      !isVesselFloatPlanReady(vessel, isLoggingOn)
    ) {
      return (
        <Box color="danger" fontSize="1">
          More details needed
        </Box>
      );
    }
    return null;
  };

  return (
    <BasicDrawerLayout
      showMenuBar={false}
      heading="Select watercraft"
      onClose={null}
      onBack={onBack}
    >
      <LayoutBody>
        <Card>
          <Card.Body>
            <NavList edge grouped>
              {vessels.map((vessel) => (
                <VesselListItem
                  key={vessel.id}
                  image={getAvatarImage(vessel)}
                  heading={getWatercraftString(vessel)}
                  description={showDescription(vessel)}
                  onClick={() => handleOnSelect(vessel)}
                />
              ))}
            </NavList>

            {vessels.length === 0 && (
              <Heading
                color="tertiary"
                textAlign="center"
                level={6}
                onClick={handleAddBoatOnClick}
              >
                Add a new watercraft
              </Heading>
            )}
            <Button.Group wide mt="3">
              <Button wide onClick={handleAddVessel} outlined>
                Add watercraft
              </Button>
            </Button.Group>
          </Card.Body>
        </Card>
      </LayoutBody>
    </BasicDrawerLayout>
  );
};

export default SelectVesselPage;
